import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Check, X, User, LogOut, Mail, Phone } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';


export default function AccountExecutiveManager() {
  const navigate = useNavigate();
  const [accountExecs, setAccountExecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [editingExec, setEditingExec] = useState(null);
  const [newExec, setNewExec] = useState({ name: '', email: '', phone: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadUser();
    fetchAccountExecutives();
  }, []);

  const loadUser = () => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      if (parsedUser.role !== 'admin' && parsedUser.role !== 'approver') {
        toast.error('Access denied. Admin or Approver role required.');
        navigate('/calculator');
      }
    } else {
      navigate('/login');
    }
  };

  const fetchAccountExecutives = async () => {
    try {
      setLoading(true);
      const response = await api.get('/account-executives');
      setAccountExecs(response.data.account_executives || []);
    } catch (error) {
      console.error('Error fetching account executives:', error);
      toast.error('Failed to fetch account executives');
    } finally {
      setLoading(false);
    }
  };

  const handleAddExec = async () => {
    if (!newExec.name.trim()) {
      toast.error('Name is required');
      return;
    }

    try {
      await api.post('/admin/account-executives', newExec);
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success('Account Executive added successfully');
      setNewExec({ name: '', email: '', phone: '' });
      setShowAddForm(false);
      fetchAccountExecutives();
    } catch (error) {
      console.error('Error adding account executive:', error);
      toast.error(error.response?.data?.detail || 'Failed to add account executive');
    }
  };

  const handleUpdateExec = async (execId, updatedData) => {
    try {
      await api.put(`/admin/account-executives/${execId}`, updatedData);
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success('Account Executive updated successfully');
      setEditingExec(null);
      fetchAccountExecutives();
    } catch (error) {
      console.error('Error updating account executive:', error);
      toast.error(error.response?.data?.detail || 'Failed to update account executive');
    }
  };

  const handleDeleteExec = async () => {
    if (!deleteConfirm) return;
    
    try {
      await api.delete(`/admin/account-executives/${deleteConfirm.id}`);
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success(`${deleteConfirm.name} deleted successfully`);
      setDeleteConfirm(null);
      fetchAccountExecutives();
    } catch (error) {
      console.error('Error deleting account executive:', error);
      toast.error('Failed to delete account executive');
      setDeleteConfirm(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(-1)}
                className="text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="text-2xl font-bold text-slate-900">Account Executive Manager</h1>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <div className="flex items-center space-x-3 px-4 py-2 bg-slate-100 rounded-full">
                  <User className="w-4 h-4 text-slate-600" />
                  <span className="text-sm font-medium text-slate-700">{user.name}</span>
                  <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full">
                    {user.role}
                  </span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="inline-flex items-center space-x-2 px-4 py-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="card p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-slate-900">Account Executives</h2>
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Add Account Executive</span>
            </button>
          </div>

          {/* Add Form */}
          {showAddForm && (
            <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                  <input
                    type="text"
                    value={newExec.name}
                    onChange={(e) => setNewExec({ ...newExec, name: e.target.value })}
                    placeholder="Enter name..."
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={newExec.email}
                    onChange={(e) => setNewExec({ ...newExec, email: e.target.value })}
                    placeholder="email@example.com"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                  <input
                    type="tel"
                    value={newExec.phone}
                    onChange={(e) => setNewExec({ ...newExec, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="input-field"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setNewExec({ name: '', email: '', phone: '' });
                  }}
                  className="px-4 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddExec}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors inline-flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Add</span>
                </button>
              </div>
            </div>
          )}

          {/* Account Executives List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <p className="mt-4 text-slate-600">Loading account executives...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  All Account Executives ({accountExecs.length})
                </h3>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-full bg-green-600"></div>
                    <span>Active ({accountExecs.filter(ae => ae.active).length})</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-full bg-gray-300"></div>
                    <span>Inactive ({accountExecs.filter(ae => !ae.active).length})</span>
                  </div>
                </div>
              </div>
              
              {accountExecs.map((exec) => (
                <div
                  key={exec.id}
                  className="border border-slate-200 rounded-lg p-4 bg-white hover:bg-slate-50 transition-colors"
                >
                  {editingExec?.id === exec.id ? (
                    // Edit Mode
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                          <input
                            type="text"
                            value={editingExec.name}
                            onChange={(e) => setEditingExec({ ...editingExec, name: e.target.value })}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                          <input
                            type="email"
                            value={editingExec.email}
                            onChange={(e) => setEditingExec({ ...editingExec, email: e.target.value })}
                            className="input-field"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                          <input
                            type="tel"
                            value={editingExec.phone}
                            onChange={(e) => setEditingExec({ ...editingExec, phone: e.target.value })}
                            className="input-field"
                          />
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingExec(null)}
                          className="px-4 py-2 bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleUpdateExec(exec.id, editingExec)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 inline-flex items-center space-x-2"
                        >
                          <Check className="w-4 h-4" />
                          <span>Save</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-center justify-between">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center space-x-3">
                          <h3 className="font-semibold text-slate-900 text-lg">{exec.name}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${exec.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {exec.active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-slate-600">
                          {exec.email && (
                            <div className="flex items-center space-x-1">
                              <Mail className="w-4 h-4" />
                              <span>{exec.email}</span>
                            </div>
                          )}
                          {exec.phone && (
                            <div className="flex items-center space-x-1">
                              <Phone className="w-4 h-4" />
                              <span>{exec.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setEditingExec(exec)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ id: exec.id, name: exec.name })}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {accountExecs.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  No account executives found. Click "Add Account Executive" to create one.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Confirm Delete
            </h3>
            <p className="text-slate-600 mb-6">
              Are you sure you want to delete <strong>"{deleteConfirm.name}"</strong>?
              <br /><br />
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteExec}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
